import { type ReactElement } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useUserEndpoints } from '@/hooks/api/useUserEndpoints';
import { useRequestAgentUpdateCheck } from '@/hooks/api/useAgentAutoUpdate';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useRoleCheck } from '@/hooks/useRoleCheck';
import { useToast } from '@/hooks/use-toast';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { roleGroups } from '@/lib/roles';
import { buildEndpointLookupPath } from '@/pages/organization/tabs/DataTableEndpointsTab/links';
import { EndpointsTable } from './EndpointsTable';

interface UserEndpointsDialogProps {
  organizationId: string;
  userId: string | null;
  userName: string;
  open: boolean;
  onClose: () => void;
}

/**
 * KIR-3035: standard inventory dialog listing every (live) endpoint a user has
 * reported. KIR-3911: rendering is delegated to the shared {@link EndpointsTable}
 * (new column order, IP/MAC line breaks, agent-version badge, no Inventory link),
 * and the backend already filters to endpoints within the org's
 * inventory-visibility window.
 *
 * KIR-5191 (`agent_auto_update` flag): each machine's update state, last
 * update check and last error, the latest release and the org switch, plus
 * "Try update now" for Owners and Managers, for all of the user's machines or
 * one. Best effort: only agents connected to the server that takes the request
 * get it; every other agent checks at its next scheduled check.
 */
export function UserEndpointsDialog({
  organizationId,
  userId,
  userName,
  open,
  onClose,
}: UserEndpointsDialogProps): ReactElement {
  // applyStaleFilter=true: the dialog hides endpoints past the visibility window.
  const { data, isLoading, isError } = useUserEndpoints(
    organizationId,
    userId,
    open,
    true
  );

  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.AGENT_AUTO_UPDATE,
    flagsmithFeatureFlags.SHARED_DEVICES,
  ]);
  const autoUpdateFlagOn =
    flags[flagsmithFeatureFlags.AGENT_AUTO_UPDATE] ?? false;
  // KIR-5412: each endpoint links to its endpoint page (person → endpoints).
  const sharedDevicesOn = flags[flagsmithFeatureFlags.SHARED_DEVICES] ?? false;
  const { hasAccess: canRequestUpdate } = useRoleCheck(
    { param: 'organizationId', anyOf: [...roleGroups.managerOrOwner] },
    organizationId
  );
  const requestCheck = useRequestAgentUpdateCheck(organizationId);
  const { toast } = useToast();

  const handleTryUpdateNow = async (machineId?: string): Promise<void> => {
    if (!userId) return;
    try {
      const { requested, reason } = await requestCheck.mutateAsync(
        machineId ? { userId, machineId } : { userId }
      );
      if (requested) {
        toast({
          title: 'Update check requested',
          description:
            'Agents that are online check within a few seconds. Any other agent checks at its next scheduled check, about every four hours.',
        });
      } else if (reason === 'paused') {
        toast({
          title: 'Automatic agent updates are paused',
          description:
            'Automatic agent updates are paused by Knostic. Nothing was sent.',
        });
      } else {
        toast({
          title: 'Automatic agent updates are off',
          description:
            'Nothing was sent. Turn on automatic agent updates to update agents.',
        });
      }
    } catch {
      toast({
        variant: 'destructive',
        title: 'Failed to request an update',
        description: 'Could not send the request. Please try again.',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            Endpoints for{' '}
            <span className="font-semibold">{userName || 'Unknown User'}</span>
          </DialogTitle>
        </DialogHeader>

        {autoUpdateFlagOn && data && (
          <div
            data-testid="endpoints-auto-update-summary"
            className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground"
          >
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span>
                Automatic agent updates:{' '}
                <span className="font-medium text-foreground">
                  {data.autoUpdateEnabled ? 'On' : 'Off'}
                </span>
              </span>
              <span>
                Latest version:{' '}
                <span className="font-medium text-foreground">
                  {data.latestAgentVersion
                    ? `v${data.latestAgentVersion}`
                    : 'Unknown'}
                </span>
              </span>
            </div>
            {canRequestUpdate && data.autoUpdateEnabled && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => void handleTryUpdateNow()}
                disabled={requestCheck.isPending || data.endpoints.length === 0}
              >
                Try update now
              </Button>
            )}
          </div>
        )}

        <EndpointsTable
          endpoints={data?.endpoints ?? []}
          isLoading={isLoading}
          isError={isError}
          showUpdateStatus={autoUpdateFlagOn}
          onTryUpdate={
            autoUpdateFlagOn && canRequestUpdate && data?.autoUpdateEnabled
              ? machineId => void handleTryUpdateNow(machineId)
              : undefined
          }
          tryUpdateDisabled={requestCheck.isPending}
          getEndpointHref={
            sharedDevicesOn
              ? machineId => buildEndpointLookupPath(organizationId, machineId)
              : undefined
          }
        />
      </DialogContent>
    </Dialog>
  );
}

UserEndpointsDialog.displayName = 'UserEndpointsDialog';
